import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor, releaseIdempotencyKey } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';
import { productQueryKey } from './useProductQuery';

/*
 * 상품 옵션명(products.option_name, catalog V135) — 옵션 선택칸 위에 보이는 이름(고도몰 es_goods.optionName, 예 "MEMBER").
 * 비어 있으면 FE 가 기본 "옵션" 으로 보인다. 이관팀이 고도몰 값 7,360건을 채웠다.
 *
 * 상세 GET 응답에 optionName(nullable)이 오고, 수정은 PATCH /admin/v1/products/{id} 로만 된다(등록 POST·전체 수정 PUT 은
 * 아직 받지 않는다). PATCH 시맨틱: 필드 생략·null = 그대로, "" = 지움.
 */

/** 저장할 PATCH 값 — 바뀌지 않았으면 undefined(요청 안 함), 비웠으면 ""(지움), 그 외 앞뒤 공백을 뺀 값. */
export const optionNamePatchValue = (
  original: string | null | undefined,
  value: string | null | undefined,
): string | undefined => {
  const before = (original ?? '').trim();
  const after = (value ?? '').trim();
  return before === after ? undefined : after;
};

export const usePatchProductOptionNameMutation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, optionName }: { productId: string; optionName: string }) => {
      const path = `/admin/v1/products/${encodeURIComponent(productId)}`;
      const body = { optionName };
      const res = await adminFetch(path, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor('PATCH', path, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await errorFromResponse(res);
      // 같은 값으로 다시 바꾸는 경우(A→B→A→B)가 첫 응답 재생으로 묻히지 않게 키를 푼다.
      releaseIdempotencyKey('PATCH', path, body);
    },
    onSuccess: (_, { productId }) => qc.invalidateQueries({ queryKey: productQueryKey(productId) }),
  });
};
